import { supabase } from '../lib/supabase';
import { getChatCompletion } from './aiIntegrations/chatCompletion';

// Helper function to convert snake_case to camelCase
function toCamelCase(obj) {
  if (!obj || typeof obj !== 'object') return obj;
  
  if (Array.isArray(obj)) {
    return obj?.map(item => toCamelCase(item));
  }
  
  const camelObj = {};
  for (const key in obj) {
    const camelKey = key?.replace(/_([a-z])/g, (_, letter) => letter?.toUpperCase());
    camelObj[camelKey] = toCamelCase(obj?.[key]);
  }
  return camelObj;
}

// Helper function to convert camelCase to snake_case
function toSnakeCase(obj) {
  if (!obj || typeof obj !== 'object') return obj;
  
  if (Array.isArray(obj)) {
    return obj?.map(item => toSnakeCase(item));
  }
  
  const snakeObj = {};
  for (const key in obj) {
    const snakeKey = key?.replace(/[A-Z]/g, letter => `_${letter?.toLowerCase()}`);
    snakeObj[snakeKey] = toSnakeCase(obj?.[key]);
  }
  return snakeObj;
}

export const aiMatchingService = {
  /**
   * Main AI matching function
   * @param {string} requestId - Purchase request ID
   * @param {string} mode - Matching mode: 'COST', 'MARGIN', 'SPEED', 'BALANCED'
   * @returns {Object} Matching result with deal info or alternatives
   */
  async matchRequestWithTraveler(requestId, mode = 'BALANCED') {
    const startTime = Date.now();
    
    try {
      // Step 1: Analyze purchase request
      const request = await this.analyzeRequest(requestId);
      
      if (!request) {
        throw new Error('Purchase request not found');
      }
      
      // Step 2: Find candidate travelers
      const candidates = await this.findCandidateTravelers(request);
      
      if (candidates?.length === 0) {
        return await this.handleNoMatch(request);
      }
      
      // Step 3: Calculate optimal match using AI
      const bestMatch = await this.calculateOptimalMatch(request, candidates, mode);
      
      // Step 4: Create deal and log
      const deal = await this.createAIMatch(request, bestMatch, mode);
      
      const executionTime = Date.now() - startTime;
      
      // Step 5: Log matching operation
      await this.logMatching({
        requestId,
        matchingMode: mode,
        candidatesCount: candidates?.length,
        selectedTravelerId: bestMatch?.travelerId,
        matchScore: bestMatch?.score,
        aiReasoning: bestMatch?.reasoning,
        executionTimeMs: executionTime
      });
      
      return {
        matched: true,
        deal,
        matchScore: bestMatch?.score,
        reasoning: bestMatch?.reasoning,
        executionTime
      };
    } catch (error) {
      console.error('AI matching error:', error);
      throw error;
    }
  },
  
  /**
   * Analyze purchase request with AI
   */
  async analyzeRequest(requestId) {
    try {
      const { data, error } = await supabase?.from('purchase_requests')?.select(`
          *,
          buyer:buyer_id (
            id,
            full_name,
            email
          )
        `)?.eq('id', requestId)?.single();
      
      if (error) throw error;
      
      // Use AI to analyze request details
      const analysisPrompt = `Analyze this purchase request and provide insights:

Item: ${data?.item_name}
Category: ${data?.category}
Route: ${data?.origin_country} → ${data?.destination_country}
Budget: ${data?.budget} KRW
Carrier Fee: ${data?.carrier_fee} KRW
Delivery Date: ${data?.delivery_date}
Notes: ${data?.notes || 'None'}

Provide:
1. Estimated weight (kg)
2. Urgency level (1-10)
3. Special handling requirements
4. Risk factors

Return JSON format only.`;
      
      const aiResponse = await getChatCompletion(
        'OPEN_AI',
        'gpt-5',
        [{ role: 'user', content: analysisPrompt }],
        {
          response_format: {
            type: 'json_schema',
            json_schema: {
              name: 'RequestAnalysis',
              schema: {
                type: 'object',
                properties: {
                  estimatedWeight: { type: 'number' },
                  urgencyLevel: { type: 'number' },
                  specialHandling: { type: 'array', items: { type: 'string' } },
                  riskFactors: { type: 'array', items: { type: 'string' } }
                },
                required: ['estimatedWeight', 'urgencyLevel', 'specialHandling', 'riskFactors'],
                additionalProperties: false
              },
              strict: true
            }
          },
          max_completion_tokens: 500
        }
      );
      
      const aiAnalysis = JSON.parse(aiResponse?.choices?.[0]?.message?.content || '{}');
      
      return {
        ...toCamelCase(data),
        aiAnalysis
      };
    } catch (error) {
      console.error('Request analysis error:', error);
      throw error;
    }
  },
  
  /**
   * Find candidate travelers based on request criteria
   */
  async findCandidateTravelers(request) {
    try {
      const { data, error } = await supabase?.from('traveler_trips')?.select(`
          *,
          traveler:traveler_id (
            id,
            full_name,
            avatar_url,
            is_verified,
            rating,
            completed_trips
          )
        `)?.eq('from_country', request?.originCountry)?.eq('to_country', request?.destinationCountry)?.gte('departure_date', request?.deliveryDate)?.eq('status', 'active')?.gte('capacity_kg', request?.aiAnalysis?.estimatedWeight || 5);
      
      if (error) throw error;
      
      // Filter by category compatibility
      const compatibleTravelers = data?.filter(trip => {
        const allowedCategories = trip?.allowed_categories || [];
        return allowedCategories?.includes(request?.category);
      });
      
      // Score each traveler with AI
      const scoredTravelers = await Promise.all(
        compatibleTravelers?.map(trip => this.scoreTraveler(trip, request))
      );
      
      // Filter travelers with score >= 0.6 (60%)
      return scoredTravelers
        ?.filter(t => t?.matchScore >= 0.6)
        ?.sort((a, b) => b?.matchScore - a?.matchScore);
    } catch (error) {
      console.error('Find candidates error:', error);
      return [];
    }
  },
  
  /**
   * Score individual traveler using AI
   */
  async scoreTraveler(trip, request) {
    try {
      const prompt = `Score this traveler for the purchase request (0.00-1.00 scale):

Request:
- Item: ${request?.itemName}
- Category: ${request?.category}
- Budget: ${request?.budget} KRW
- Carrier Fee: ${request?.carrierFee} KRW
- Delivery Date: ${request?.deliveryDate}
- Estimated Weight: ${request?.aiAnalysis?.estimatedWeight || 5} kg
- Urgency: ${request?.aiAnalysis?.urgencyLevel || 5}/10

Traveler:
- Rating: ${trip?.traveler?.rating || 0}/5
- Completed Trips: ${trip?.traveler?.completed_trips || 0}
- Verified: ${trip?.traveler?.is_verified ? 'Yes' : 'No'}
- Capacity: ${trip?.capacity_kg} kg
- Departure: ${trip?.departure_date}
- Return: ${trip?.return_date}

Consider:
1. Route compatibility (40%)
2. Capacity match (20%)
3. Reliability (rating, history) (20%)
4. Timing (delivery speed) (20%)

Return JSON with score (0.00-1.00), reasoning, and risks.`;
      
      const aiResponse = await getChatCompletion(
        'OPEN_AI',
        'gpt-5',
        [{ role: 'user', content: prompt }],
        {
          response_format: {
            type: 'json_schema',
            json_schema: {
              name: 'TravelerScore',
              schema: {
                type: 'object',
                properties: {
                  score: { type: 'number' },
                  reasoning: { type: 'string' },
                  risks: { type: 'array', items: { type: 'string' } }
                },
                required: ['score', 'reasoning', 'risks'],
                additionalProperties: false
              },
              strict: true
            }
          },
          max_completion_tokens: 500
        }
      );
      
      const scoring = JSON.parse(aiResponse?.choices?.[0]?.message?.content || '{}');
      
      return {
        ...toCamelCase(trip),
        matchScore: scoring?.score || 0,
        matchReasoning: scoring?.reasoning || '',
        matchRisks: scoring?.risks || []
      };
    } catch (error) {
      console.error('Score traveler error:', error);
      return {
        ...toCamelCase(trip),
        matchScore: 0,
        matchReasoning: 'Scoring failed',
        matchRisks: ['AI scoring error']
      };
    }
  },
  
  /**
   * Calculate optimal match based on mode
   */
  async calculateOptimalMatch(request, candidates, mode) {
    try {
      const modeWeights = {
        COST: { price: 0.6, speed: 0.1, reliability: 0.2, route: 0.1 },
        MARGIN: { price: 0.1, speed: 0.1, reliability: 0.2, route: 0.6 },
        SPEED: { price: 0.1, speed: 0.6, reliability: 0.2, route: 0.1 },
        BALANCED: { price: 0.25, speed: 0.25, reliability: 0.25, route: 0.25 }
      };
      
      const weights = modeWeights?.[mode] || modeWeights?.BALANCED;
      
      const prompt = `Select the best traveler for this purchase request.

Mode: ${mode}
Weights: Price ${weights?.price * 100}%, Speed ${weights?.speed * 100}%, Reliability ${weights?.reliability * 100}%, Route ${weights?.route * 100}%

Request:
- Item: ${request?.itemName}
- Budget: ${request?.budget} KRW
- Carrier Fee: ${request?.carrierFee} KRW
- Delivery Date: ${request?.deliveryDate}

Candidates:
${candidates?.slice(0, 5)?.map((c, i) => `
${i + 1}. Traveler ID: ${c?.traveler?.id}
   - Match Score: ${c?.matchScore}
   - Rating: ${c?.traveler?.rating || 0}/5
   - Completed Trips: ${c?.traveler?.completed_trips || 0}
   - Verified: ${c?.traveler?.is_verified ? 'Yes' : 'No'}
   - Capacity: ${c?.capacityKg} kg
   - Departure: ${c?.departureDate}
   - Reasoning: ${c?.matchReasoning}
`)?.join('')}

Return the best traveler ID, final score (0.00-1.00), reasoning, and recommended carrier fee.`;
      
      const aiResponse = await getChatCompletion(
        'OPEN_AI',
        'gpt-5',
        [{ role: 'user', content: prompt }],
        {
          response_format: {
            type: 'json_schema',
            json_schema: {
              name: 'OptimalMatch',
              schema: {
                type: 'object',
                properties: {
                  travelerId: { type: 'string' },
                  score: { type: 'number' },
                  reasoning: { type: 'string' },
                  recommendedFee: { type: 'number' }
                },
                required: ['travelerId', 'score', 'reasoning', 'recommendedFee'],
                additionalProperties: false
              },
              strict: true
            }
          },
          max_completion_tokens: 800
        }
      );
      
      const result = JSON.parse(aiResponse?.choices?.[0]?.message?.content || '{}');
      
      return {
        travelerId: result?.travelerId,
        score: result?.score || 0,
        reasoning: result?.reasoning || '',
        recommendedFee: result?.recommendedFee || request?.carrierFee
      };
    } catch (error) {
      console.error('Calculate optimal match error:', error);
      // Fallback: return highest scored candidate
      const best = candidates?.[0];
      return {
        travelerId: best?.traveler?.id,
        score: best?.matchScore || 0,
        reasoning: 'Fallback to highest scored candidate',
        recommendedFee: request?.carrierFee
      };
    }
  },
  
  /**
   * Create AI-matched deal
   */
  async createAIMatch(request, bestMatch, mode) {
    try {
      // Create deal directly (bypass offer system for AI matching)
      const dealData = {
        buyer_id: request?.buyerId,
        traveler_id: bestMatch?.travelerId,
        request_id: request?.id,
        status: 'ai_matched',
        matching_mode: mode,
        ai_match_score: bestMatch?.score,
        ai_reasoning: bestMatch?.reasoning,
        is_ai_matched: true
      };
      
      const { data, error } = await supabase?.from('deals')?.insert(dealData)?.select(`
          *,
          buyer:buyer_id (id, full_name, email),
          traveler:traveler_id (id, full_name, email, avatar_url)
        `)?.single();
      
      if (error) throw error;
      
      // Update request status
      await supabase?.from('purchase_requests')?.update({ status: 'matched' })?.eq('id', request?.id);
      
      // Log pricing recommendation
      await this.logPricing({
        dealId: data?.id,
        requestId: request?.id,
        recommendedFee: bestMatch?.recommendedFee,
        platformCommission: bestMatch?.recommendedFee * 0.2,
        discountApplied: 0,
        pricingReasoning: { mode, aiRecommendation: bestMatch?.reasoning }
      });
      
      return toCamelCase(data);
    } catch (error) {
      console.error('Create AI match error:', error);
      throw error;
    }
  },
  
  /**
   * Handle no match scenario
   */
  async handleNoMatch(request) {
    try {
      const prompt = `No traveler match found for this request. Suggest alternatives:

Request:
- Item: ${request?.itemName}
- Route: ${request?.originCountry} → ${request?.destinationCountry}
- Delivery Date: ${request?.deliveryDate}
- Budget: ${request?.budget} KRW

Suggest:
1. Similar routes
2. Extended timeline options
3. Alternative delivery methods
4. Budget adjustments

Return JSON with alternatives array.`;
      
      const aiResponse = await getChatCompletion(
        'OPEN_AI',
        'gpt-5',
        [{ role: 'user', content: prompt }],
        {
          response_format: {
            type: 'json_schema',
            json_schema: {
              name: 'NoMatchAlternatives',
              schema: {
                type: 'object',
                properties: {
                  alternatives: {
                    type: 'array',
                    items: {
                      type: 'object',
                      properties: {
                        type: { type: 'string' },
                        suggestion: { type: 'string' }
                      },
                      required: ['type', 'suggestion'],
                      additionalProperties: false
                    }
                  }
                },
                required: ['alternatives'],
                additionalProperties: false
              },
              strict: true
            }
          },
          max_completion_tokens: 600
        }
      );
      
      const result = JSON.parse(aiResponse?.choices?.[0]?.message?.content || '{}');
      
      return {
        matched: false,
        alternatives: result?.alternatives || []
      };
    } catch (error) {
      console.error('Handle no match error:', error);
      return {
        matched: false,
        alternatives: [
          { type: 'timeline', suggestion: 'Extend delivery date by 7-14 days' },
          { type: 'route', suggestion: 'Consider nearby airports or cities' },
          { type: 'budget', suggestion: 'Increase carrier fee by 20-30%' }
        ]
      };
    }
  },
  
  /**
   * Log matching operation
   */
  async logMatching(logData) {
    try {
      const snakeData = toSnakeCase(logData);
      await supabase?.from('ai_matching_logs')?.insert(snakeData);
    } catch (error) {
      console.error('Log matching error:', error);
    }
  },
  
  /**
   * Log pricing recommendation
   */
  async logPricing(logData) {
    try {
      const snakeData = toSnakeCase(logData);
      await supabase?.from('ai_pricing_logs')?.insert(snakeData);
    } catch (error) {
      console.error('Log pricing error:', error);
    }
  },
  
  /**
   * Get matching logs for a request
   */
  async getMatchingLogs(requestId) {
    try {
      const { data, error } = await supabase?.from('ai_matching_logs')?.select('*')?.eq('request_id', requestId)?.order('created_at', { ascending: false });
      
      if (error) throw error;
      return toCamelCase(data || []);
    } catch (error) {
      console.error('Get matching logs error:', error);
      return [];
    }
  }
};